import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { QRCodeSVG } from "qrcode.react";
import fbrLogo from "../assets/fbr.png";
import { getInvoiceDesignApi, saveInvoiceDesignApi, uploadInvoiceDesignAssetApi } from "../api";
import { useData } from "../ContextApi/ContextProvider";
import {
  DESIGN_CANVAS_HEIGHT,
  DESIGN_CANVAS_WIDTH,
  DEFAULT_BREAKDOWN_ROWS,
  INVOICE_ITEM_FIELD_OPTIONS,
  defaultInvoiceDesignTemplate,
  loadLocalInvoiceDesign,
  saveLocalInvoiceDesign,
} from "../utils/invoiceDesign";

const MIN_BLOCK_WIDTH = 28;
const MIN_BLOCK_HEIGHT = 16;
const LOGO_WHITE_THRESHOLD = 246;
const LOGO_EDGE_PADDING = 18;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const isNearWhitePixel = (r, g, b) => r >= LOGO_WHITE_THRESHOLD && g >= LOGO_WHITE_THRESHOLD && b >= LOGO_WHITE_THRESHOLD;

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not read logo image."));
    };
    image.src = objectUrl;
  });

const prepareLogoUploadFile = async (file) => {
  if (!file?.type?.startsWith("image/")) return file;

  try {
    const image = await loadImageFromFile(file);
    const sourceCanvas = document.createElement("canvas");
    const sourceContext = sourceCanvas.getContext("2d", { willReadFrequently: true });
    if (!sourceContext || !image.naturalWidth || !image.naturalHeight) return file;

    sourceCanvas.width = image.naturalWidth;
    sourceCanvas.height = image.naturalHeight;
    sourceContext.drawImage(image, 0, 0);

    const imageData = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
    const { data, width, height } = imageData;
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = (y * width + x) * 4;
        const alpha = data[index + 3];
        const isContent = alpha > 10 && !isNearWhitePixel(data[index], data[index + 1], data[index + 2]);
        if (isContent) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    if (maxX < minX || maxY < minY) return file;

    const cropX = Math.max(0, minX - LOGO_EDGE_PADDING);
    const cropY = Math.max(0, minY - LOGO_EDGE_PADDING);
    const cropRight = Math.min(width - 1, maxX + LOGO_EDGE_PADDING);
    const cropBottom = Math.min(height - 1, maxY + LOGO_EDGE_PADDING);
    const cropWidth = cropRight - cropX + 1;
    const cropHeight = cropBottom - cropY + 1;
    const croppedData = sourceContext.getImageData(cropX, cropY, cropWidth, cropHeight);

    for (let i = 0; i < croppedData.data.length; i += 4) {
      if (isNearWhitePixel(croppedData.data[i], croppedData.data[i + 1], croppedData.data[i + 2])) {
        croppedData.data[i + 3] = 0;
      }
    }

    const outputCanvas = document.createElement("canvas");
    const outputContext = outputCanvas.getContext("2d");
    if (!outputContext) return file;

    outputCanvas.width = cropWidth;
    outputCanvas.height = cropHeight;
    outputContext.putImageData(croppedData, 0, 0);

    const blob = await new Promise((resolve) => outputCanvas.toBlob(resolve, "image/png"));
    if (!blob) return file;

    const cleanName = file.name.replace(/\.[^.]+$/, "") || "company-logo";
    return new File([blob], `${cleanName}-clean.png`, { type: "image/png" });
  } catch {
    return file;
  }
};

const invoiceLine = (width = 82) => (
  <span style={{ display: "inline-block", minWidth: width, borderBottom: "1.2px solid #000", height: 14, padding: "0 5px" }} />
);

const getDesignFieldLabel = (key) => INVOICE_ITEM_FIELD_OPTIONS.find((field) => field.key === key)?.label || key;

const formatPreviewMoney = (value) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getPreviewItemValue = (item, key) => {
  const valueExcl = Number(item.valueSalesExcludingST || 0);
  const quantity = Number(item.quantity || 0);
  const tax = Number(item.salesTaxApplicable || 0);
  const furtherTax = Number(item.furtherTax || 0);
  const discount = Number(item.discount || 0);
  const moneyFields = new Set([
    "unitPrice",
    "lineTotal",
    "totalValues",
    "valueSalesExcludingST",
    "fixedNotifiedValueOrRetailPrice",
    "salesTaxApplicable",
    "salesTaxWithheldAtSource",
    "extraTax",
    "furtherTax",
    "fedPayable",
    "discount",
    "totalValueIncludingSalesTax",
  ]);

  if (key === "sr") return item.sr;
  if (key === "unitPrice") return formatPreviewMoney(quantity ? valueExcl / quantity : item.fixedNotifiedValueOrRetailPrice);
  if (key === "lineTotal") return formatPreviewMoney(valueExcl + discount);
  if (key === "totalValueIncludingSalesTax") return formatPreviewMoney(valueExcl + tax + furtherTax - discount);
  if (key === "quantity") return item.quantity;
  if (moneyFields.has(key)) return formatPreviewMoney(item[key]);
  return item[key] || "";
};

const blockFrameSx = (block, selectedId, preview, hasLetterhead) => ({
  position: "absolute",
  left: block.x,
  top: block.y,
  width: block.w,
  height: block.h,
  border: preview ? "none" : selectedId === block.id ? "2px solid #2563eb" : "1px solid #cbd5e1",
  bgcolor: hasLetterhead ? "transparent" : "#fff",
  p: preview ? 0 : 0.5,
  overflow: "hidden",
  cursor: preview ? "default" : "pointer",
  color: "#000",
});

const renderInvoiceBlock = (block, template) => {
  if (block.id === "company_logo") {
    return template.logo ? (
      <img src={template.logo} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain", transform: "scale(1.14)" }} />
    ) : (
      <Box sx={{ height: "100%", display: "grid", placeItems: "center", color: "#64748b", fontFamily: "Inter, sans-serif", fontSize: 11 }}>
        Logo
      </Box>
    );
  }

  if (block.id === "invoice_title") {
    return (
      <Box sx={{ display: "inline-block", border: "2px solid #000", px: 1.5, py: 0.6, fontWeight: 950, fontSize: 13, lineHeight: 1, whiteSpace: "nowrap" }}>
        SALES TAX INVOICE
      </Box>
    );
  }

  if (block.id === "invoice_meta") {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr", gap: 2, borderBottom: "1.4px solid #000", pb: 0.5, fontSize: 10.5 }}>
        <span><b>Serial No.:</b> <span style={{ borderBottom: "1.2px solid #000", display: "inline-block", minWidth: 74 }}>Manual1</span></span>
        <span><b>Date:</b> <span style={{ borderBottom: "1.2px solid #000", display: "inline-block", minWidth: 88 }}>2025-08-04</span></span>
        <span><b>Time of Supply:</b> {invoiceLine(72)}</span>
      </Box>
    );
  }

  if (block.id === "seller_info") {
    return (
      <Box sx={{ lineHeight: 1.45, fontSize: 10.5 }}>
        <div><b>Supplier's Name:</b> <b><u>Shezan</u></b></div>
        <div><b>Address:</b> Lahore</div>
        <div><b>National Tax No.:</b> <b><u>3510203007795</u></b></div>
        <div><b>Terms of Sales:</b> {invoiceLine(148)}</div>
      </Box>
    );
  }

  if (block.id === "buyer_info") {
    return (
      <Box sx={{ lineHeight: 1.45, fontSize: 10.5 }}>
        <div><b>Buyer's Name:</b> <b><u>Shezan</u></b></div>
        <div><b>Address:</b> Lahore</div>
        <div><b>National Tax No.:</b> <b><u>3510203007795</u></b></div>
      </Box>
    );
  }

  if (block.id === "description_code") {
    return <Box sx={{ textAlign: "right", fontSize: 9.5 }}>(Description code: )</Box>;
  }

  if (block.id === "items_table") {
    return (
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
          fontSize: 8.5,
          lineHeight: 1.05,
          "& th, & td": { border: "1.2px solid #000", p: "1px 2px", height: "auto" },
          "& th": { textAlign: "center", fontWeight: 900, lineHeight: 1.05 },
          "& td": { textAlign: "right", lineHeight: 1.05 },
          "& td:nth-of-type(2)": { textAlign: "left" },
        }}
      >
        <thead>
          <tr>
            <th rowSpan="2">Qty</th>
            <th rowSpan="2">Description</th>
            <th rowSpan="2">Hs Code</th>
            <th rowSpan="2">Price</th>
            <th colSpan="2">Value Exclusive<br />Sales Tax</th>
            <th rowSpan="2">Rate of<br />Sales Tax</th>
            <th colSpan="2">Total Sales Tax<br />Payable</th>
            <th colSpan="2">Value Inclusive<br />Sales Tax</th>
          </tr>
          <tr><th>Rs.</th><th>Ps.</th><th>Rs.</th><th>Ps.</th><th>Rs.</th><th>Ps.</th></tr>
        </thead>
        <tbody>
          <tr><td>1</td><td>nothing</td><td>8432.1010</td><td>2</td><td>2</td><td>00</td><td>21</td><td>1</td><td>00</td><td>3</td><td>00</td></tr>
          <tr><td colSpan="4"><b>Total</b></td><td><b>2</b></td><td><b>00</b></td><td></td><td><b>1</b></td><td><b>00</b></td><td><b>3</b></td><td><b>00</b></td></tr>
        </tbody>
      </Box>
    );
  }

  if (block.id === "fbr_invoice") {
    return <Box sx={{ fontSize: 10.5 }}><b>FBR Invoice #:</b> 3dsd-sddsds-dsd</Box>;
  }

  if (block.id === "verification") {
    return (
      <Stack direction="row" spacing={2} alignItems="center" sx={{ height: "100%" }}>
        <img src={fbrLogo} alt="FBR" style={{ width: 96, height: 70, objectFit: "contain" }} />
        <QRCodeSVG value="3dsd-sddsds-dsd" size={70} />
      </Stack>
    );
  }

  if (block.id === "totals") {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 48px", gap: "8px", textAlign: "right", fontSize: 10.5, lineHeight: 1.6 }}>
        <b>Sales Tax:</b><span>1</span>
        <b>Net Tax Inc.Value:</b><span>3</span>
      </Box>
    );
  }

  if (block.id === "signature") {
    return (
      <Box sx={{ fontSize: 10.5, lineHeight: 1.35 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
          <b>Signature:</b>
          <Box sx={{ flex: 1, borderBottom: "1.2px solid #000", height: 14 }} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
          <b>Name &amp; Designation:</b>
          <Box sx={{ flex: 1, borderBottom: "1.2px solid #000", height: 14 }} />
        </Box>
      </Box>
    );
  }

  return null;
};

const ModernInvoiceDesignPreview = ({ template, globalData }) => {
  const isVisible = (blockId) => template.blocks.find((block) => block.id === blockId)?.visible !== false;
  const sellerName = globalData?.company_name || globalData?.full_name || "Seller Business";
  const sellerRegistration = globalData?.ntn_cnic || "Registration No.";
  const sellerAddress = globalData?.business_address || globalData?.city || "Seller address";
  const sellerProvince = globalData?.province || "Province";
  const sellerEmail = template.sellerContactEmail || globalData?.email || "seller@email.com";
  const sellerPhone = template.sellerContactPhone || globalData?.phone || globalData?.mobile || "03000000000";
  const showSellerContact = isVisible("seller_contact");
  const visibleItemColumns = [
    ...(Array.isArray(template.itemColumns) ? template.itemColumns : []).filter((column) => column.visible !== false),
    ...(Array.isArray(template.customItemColumns) ? template.customItemColumns : []).filter((column) => column.visible !== false),
  ];
  const visibleBreakdownRows = (Array.isArray(template.breakdownRows) ? template.breakdownRows : DEFAULT_BREAKDOWN_ROWS).filter((row) => row.visible !== false && row.valueType !== "words");
  const showTotalBreakdown = visibleBreakdownRows.length > 0;
  const showAmountWords = (Array.isArray(template.breakdownRows) ? template.breakdownRows : DEFAULT_BREAKDOWN_ROWS).find((row) => row.id === "amountWords")?.visible !== false;
  const previewItems = [
    { sr: 1, hsCode: "5603.1200", productDescription: "Non Woven Bags (15 x 19)", quantity: "132.48", fixedNotifiedValueOrRetailPrice: 820, valueSalesExcludingST: 108633.6, rate: "18.00%", salesTaxApplicable: 19554.05, discount: 1250, furtherTax: 0, extraTax: 0, fedPayable: 0, salesTaxWithheldAtSource: 0, saleType: "Goods at standard rate" },
    { sr: 2, hsCode: "0101.2100", productDescription: "Sample taxable item", quantity: "25", fixedNotifiedValueOrRetailPrice: 1500, valueSalesExcludingST: 37500, rate: "18.00%", salesTaxApplicable: 6750, discount: 0, furtherTax: 0, extraTax: 0, fedPayable: 0, salesTaxWithheldAtSource: 0, saleType: "Goods at standard rate" },
    { sr: 3, hsCode: "3923.2100", productDescription: "Packing material", quantity: "40", fixedNotifiedValueOrRetailPrice: 550, valueSalesExcludingST: 22000, rate: "18.00%", salesTaxApplicable: 3960, discount: 500, furtherTax: 0, extraTax: 0, fedPayable: 0, salesTaxWithheldAtSource: 0, saleType: "Goods at standard rate" },
  ];
  const previewTotals = {
    salesBeforeDiscount: 169383.6,
    totalExcl: 167633.6,
    discount: 1750,
    totalTax: 30264.05,
    furtherTax: 0,
    extraTax: 0,
    fedPayable: 0,
    salesTaxWithheldAtSource: 0,
    incomeTax236G236H: 2,
    incomeTaxAdd: 2,
    grossInvoiceValue: 197897.65,
    netPayableAmount: 197897.65,
  };

  return (
    <>
      <Box sx={{ position: "absolute", left: 35, top: 42, fontFamily: "Arial, sans-serif" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#7c8795", fontSize: 14, fontWeight: 800, lineHeight: 1 }}>
          <Box sx={{ width: 24, height: 24, borderLeft: "5px solid #5bb970", borderBottom: "5px solid #5bb970", borderTop: "5px solid #3266ad", borderRight: "5px solid #3266ad", borderRadius: "0 12px 12px 0" }} />
          <span>DIGITAL<br />INVOICING</span>
        </Box>
        <Box sx={{ mt: 1.4, fontSize: 11.5, lineHeight: 1.3 }}>
          <Box sx={{ fontWeight: 900, mb: 0.7 }}>{sellerName}</Box>
          {showSellerContact && (
            <>
            <Box>{sellerEmail}</Box>
            <Box>{sellerPhone}</Box>
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ position: "absolute", right: 51, top: 44, textAlign: "right", fontFamily: "Arial, sans-serif" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <img src={fbrLogo} alt="FBR" style={{ width: 82, height: 68, objectFit: "contain" }} />
          <QRCodeSVG value="sample-fbr-invoice" size={62} />
        </Box>
      </Box>
      <Typography sx={{ position: "absolute", top: 178, width: "100%", textAlign: "center", fontFamily: "Arial, sans-serif", fontSize: 15, fontWeight: 850 }}>
        Sales Tax Invoice
      </Typography>
      <Box sx={{ position: "absolute", left: 35, right: 35, top: 224, display: "grid", gridTemplateColumns: "1.05fr 1.05fr 0.95fr", gap: 4.2, fontFamily: "Arial, sans-serif", fontSize: 8.2, lineHeight: 1.25 }}>
        {[
          ["Seller Information", [["Business Name:", sellerName], ["Registration No.:", sellerRegistration], ["STRN No.:", "STRN-001"], ["Address:", sellerAddress], ["Province:", sellerProvince]]],
          ["Buyer Information", [["Business Name:", "Buyer Business"], ["Registration No.:", "Buyer NTN/CNIC"], ["STRN No.:", "STRN-002"], ["Address:", "Buyer address"], ["Province:", "Buyer province"]]],
          ["Invoice Summary", [["FBR Invoice No.:", "N/A"], ["Seller Invoice No.:", "INV-ST-26-1039"], ["Invoice Date:", "04-08-2025"], ["Invoice Type:", "Sale Invoice"], ["Tax Period:", "08/2026"]]],
        ].map(([title, rows]) => (
          <Box key={title}>
            <Box sx={{ fontSize: 11.5, fontWeight: 900, mb: 1.8 }}>{title}</Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "76px 1fr", gap: "4px 9px" }}>
              {rows.map(([label, value]) => (
                <React.Fragment key={label}>
                  <Box sx={{ fontWeight: 900 }}>{label}</Box>
                  <Box>{value}</Box>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ position: "absolute", left: 26, right: 26, top: 354, fontFamily: "Arial, sans-serif", fontSize: 8.5 }}>
        <Box sx={{ fontSize: 11.5, fontWeight: 900, mb: 1.4 }}>Item Details</Box>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", "& th, & td": { border: "1px solid #c9c9c9", p: "6px 5px" }, "& th": { bgcolor: "#f7f7f7", fontWeight: 900, fontSize: 5.9, textAlign: "center" }, "& td": { fontSize: 5.9, textAlign: "right" }, "& td:nth-of-type(3)": { textAlign: "left" } }}>
          <thead>
            <tr>
              {visibleItemColumns.map((column) => (
                <th key={column.id || column.key}>{column.label || getDesignFieldLabel(column.key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewItems.map((item) => (
              <tr key={item.sr}>
                {visibleItemColumns.map((column) => (
                  <td key={`${item.sr}-${column.id || column.key}`}>{column.id ? column.value : getPreviewItemValue(item, column.key)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1.23fr 1fr", gap: 2.2, mt: 4.2 }}>
          <Box>
            {isVisible("invoice_note") && (
              <Box sx={{ border: "1px solid #c9c9c9", mb: 2 }}>
                <Box sx={{ bgcolor: "#f7f7f7", p: "7px 8px", fontWeight: 900, borderBottom: "1px solid #c9c9c9", fontSize: 8.5 }}>INVOICE NOTE</Box>
                <Box sx={{ minHeight: 54, p: 1, fontSize: 8, lineHeight: 1.35 }}>
                  Note: It is pertinent to mention that we hold an Exemption Certificate under Section 159(1)/153 of the Income Tax Ordinance, 2001.
                </Box>
              </Box>
            )}
            {isVisible("additional_info") && (
              <Box sx={{ border: "1px solid #c9c9c9" }}>
                <Box sx={{ bgcolor: "#f7f7f7", p: "7px 8px", fontWeight: 900, borderBottom: "1px solid #c9c9c9", fontSize: 8.5 }}>ADDITIONAL INFORMATION</Box>
                {[
                  ["Terms of Sale:", "Credit"],
                  ["Invoice Ref No.:", "INV-ST-26-1039"],
                  ["Buyer Registration Type:", "Registered"],
                  ["Scenario ID:", "SN001"],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ display: "grid", gridTemplateColumns: "122px 1fr", borderTop: "1px solid #d8d8d8", minHeight: 18, alignItems: "center" }}>
                    <Box sx={{ p: "4px 8px", color: "#374151", fontWeight: 900 }}>{label}</Box>
                    <Box sx={{ p: "4px 8px" }}>{value}</Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box>
            {showTotalBreakdown && (
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", "& th, & td": { border: "1px solid #c9c9c9", p: "7px" }, "& th": { bgcolor: "#f7f7f7", textAlign: "left", fontWeight: 900, fontSize: 8.5 }, "& td": { fontSize: 8 }, "& td:last-child": { textAlign: "right" } }}>
                <thead><tr><th>Total Breakdown</th><th>Value (PKR)</th></tr></thead>
                <tbody>
                  {visibleBreakdownRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.isNet ? <b>{row.label}</b> : row.label}</td>
                      <td>{row.isNet ? <b>{row.valueType === "custom" ? row.value : formatPreviewMoney(previewTotals[row.id])}</b> : row.valueType === "custom" ? row.value : formatPreviewMoney(previewTotals[row.id])}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            )}
            {showAmountWords && (
              <Box sx={{ border: "1px solid #c9c9c9", borderTop: showTotalBreakdown ? 0 : "1px solid #c9c9c9", p: 1, fontSize: 7.2, lineHeight: 1.1 }}>
                <b>Net Payable Amount (In Words):</b><br />
                One Hundred Twenty Eight Thousand One Hundred Eighty Seven Rupees and Sixty Five Paisa
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

const InvoicePreviewCanvas = ({ template, selectedId, onSelect, onBlockChange, globalData, preview = false }) => {
  const canvasRef = useRef(null);

  const startBlockGesture = (event, block, mode) => {
    if (preview) return;

    event.preventDefault();
    event.stopPropagation();
    onSelect?.(block.id);

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = DESIGN_CANVAS_WIDTH / rect.width;
    const scaleY = DESIGN_CANVAS_HEIGHT / rect.height;
    const startX = event.clientX;
    const startY = event.clientY;
    const startBlock = { ...block };
    const pointerId = event.pointerId;

    event.currentTarget.setPointerCapture?.(pointerId);

    const handleMove = (moveEvent) => {
      const dx = (moveEvent.clientX - startX) * scaleX;
      const dy = (moveEvent.clientY - startY) * scaleY;

      if (mode === "resize") {
        const nextW = clamp(Math.round(startBlock.w + dx), MIN_BLOCK_WIDTH, DESIGN_CANVAS_WIDTH - startBlock.x);
        const nextH = clamp(Math.round(startBlock.h + dy), MIN_BLOCK_HEIGHT, DESIGN_CANVAS_HEIGHT - startBlock.y);
        onBlockChange?.(block.id, { w: nextW, h: nextH });
        return;
      }

      const nextX = clamp(Math.round(startBlock.x + dx), 0, DESIGN_CANVAS_WIDTH - startBlock.w);
      const nextY = clamp(Math.round(startBlock.y + dy), 0, DESIGN_CANVAS_HEIGHT - startBlock.h);
      onBlockChange?.(block.id, { x: nextX, y: nextY });
    };

    const handleUp = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
  };

  return (
    <Box
      ref={canvasRef}
      sx={{
        width: DESIGN_CANVAS_WIDTH,
        height: DESIGN_CANVAS_HEIGHT,
        mx: "auto",
        bgcolor: "#fff",
        border: preview ? "1px solid #94a3b8" : "1px solid #cbd5e1",
        boxShadow: preview ? "0 22px 55px rgba(15, 23, 42, 0.16)" : "0 18px 42px rgba(15, 23, 42, 0.12)",
        position: "relative",
        flex: "0 0 auto",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#000",
        fontSize: 10.5,
        userSelect: preview ? "auto" : "none",
        touchAction: preview ? "auto" : "none",
      }}
    >
      <ModernInvoiceDesignPreview template={template} globalData={globalData} />
      {template.blocks.filter((block) => block.id === "company_logo" && block.visible).map((block) => (
        <Box
          key={block.id}
          onPointerDown={(event) => startBlockGesture(event, block, "move")}
          sx={{
            ...blockFrameSx(block, selectedId, preview, !!template.letterhead),
            zIndex: selectedId === block.id ? 2 : 1,
            cursor: preview ? "default" : "move",
          }}
        >
          {renderInvoiceBlock(block, template)}
          {!preview && selectedId === block.id && (
            <Box
              onPointerDown={(event) => startBlockGesture(event, block, "resize")}
              sx={{
                position: "absolute",
                right: -1,
                bottom: -1,
                width: 12,
                height: 12,
                border: "2px solid #fff",
                bgcolor: "#2563eb",
                cursor: "nwse-resize",
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.24)",
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

const Design = () => {
  const { globalData } = useData();
  const [template, setTemplate] = useState(() => loadLocalInvoiceDesign(globalData));
  const [selectedId, setSelectedId] = useState("company_logo");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const selectedBlock = useMemo(
    () => template.blocks.find((block) => block.id === selectedId) || template.blocks[0],
    [template.blocks, selectedId]
  );

  useEffect(() => {
    let active = true;

    const loadSavedDesign = async () => {
      setSaveState("loading");
      setTemplate(loadLocalInvoiceDesign(globalData));

      try {
        const savedDesign = await getInvoiceDesignApi();
        if (!active) return;
        if (!savedDesign?.blocks) {
          setSaveState("idle");
          setStatusMessage("");
          return;
        }
        const normalized = saveLocalInvoiceDesign(savedDesign, globalData);
        setTemplate(normalized);
        setStatusMessage("Loaded saved client design.");
        setSaveState("idle");
      } catch (error) {
        if (!active) return;
        setStatusMessage("Using local design until backend design storage is available.");
        setSaveState("local");
      }
    };

    loadSavedDesign();

    return () => {
      active = false;
    };
  }, [globalData]);

  const updateBlock = (id, patch) => {
    setTemplate((prev) => ({
      ...prev,
      blocks: prev.blocks.map((block) => (block.id === id ? { ...block, ...patch } : block)),
    }));
  };

  const updateDesignValue = (patch) => {
    setTemplate((prev) => ({ ...prev, ...patch }));
  };

  const updateItemColumn = (key, patch) => {
    setTemplate((prev) => ({
      ...prev,
      itemColumns: prev.itemColumns.map((column) => (column.key === key ? { ...column, ...patch } : column)),
    }));
  };

  const addCustomItemColumn = () => {
    setTemplate((prev) => ({
      ...prev,
      customItemColumns: [
        ...(prev.customItemColumns || []),
        { id: `custom_item_${Date.now()}`, label: "Custom Column", value: "", visible: true },
      ],
    }));
  };

  const updateCustomItemColumn = (id, patch) => {
    setTemplate((prev) => ({
      ...prev,
      customItemColumns: (prev.customItemColumns || []).map((column) => (column.id === id ? { ...column, ...patch } : column)),
    }));
  };

  const removeCustomItemColumn = (id) => {
    setTemplate((prev) => ({
      ...prev,
      customItemColumns: (prev.customItemColumns || []).filter((column) => column.id !== id),
    }));
  };

  const updateBreakdownRow = (id, patch) => {
    setTemplate((prev) => ({
      ...prev,
      breakdownRows: prev.breakdownRows.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  };

  const addCustomBreakdownRow = () => {
    setTemplate((prev) => ({
      ...prev,
      breakdownRows: [
        ...(prev.breakdownRows || []),
        { id: `custom_breakdown_${Date.now()}`, label: "Custom Breakdown", value: "", visible: true, valueType: "custom" },
      ],
    }));
  };

  const removeCustomBreakdownRow = (id) => {
    setTemplate((prev) => ({
      ...prev,
      breakdownRows: (prev.breakdownRows || []).filter((row) => row.id !== id),
    }));
  };

  const updateBlockWithFlow = (id, patch) => {
    setTemplate((prev) => {
      const sourceBlock = prev.blocks.find((block) => block.id === id);
      if (!sourceBlock) return prev;

      const nextSourceBlock = { ...sourceBlock, ...patch };
      const heightDelta = id === "items_table" && patch.h !== undefined
        ? Number(nextSourceBlock.h || 0) - Number(sourceBlock.h || 0)
        : 0;
      const shouldMoveLowerBlocks = heightDelta !== 0;

      return {
        ...prev,
        blocks: prev.blocks.map((block) => {
          if (block.id === id) {
            return id === "company_logo" ? { ...nextSourceBlock, userPositioned: true } : nextSourceBlock;
          }
          if (shouldMoveLowerBlocks && Number(block.y || 0) > Number(sourceBlock.y || 0)) {
            return {
              ...block,
              y: clamp(Number(block.y || 0) + heightDelta, 0, DESIGN_CANVAS_HEIGHT - Number(block.h || 0)),
            };
          }
          return block;
        }),
      };
    });
  };

  const moveSelected = (axis, value) => {
    if (!selectedBlock) return;
    const bounds = {
      x: [0, DESIGN_CANVAS_WIDTH - selectedBlock.w],
      y: [0, DESIGN_CANVAS_HEIGHT - selectedBlock.h],
      w: [MIN_BLOCK_WIDTH, DESIGN_CANVAS_WIDTH - selectedBlock.x],
      h: [MIN_BLOCK_HEIGHT, DESIGN_CANVAS_HEIGHT - selectedBlock.y],
    };
    const [min, max] = bounds[axis] || [0, DESIGN_CANVAS_WIDTH];
    updateBlockWithFlow(selectedBlock.id, { [axis]: clamp(Number(value) || 0, min, max) });
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setSaveState("saving");
      setStatusMessage("Preparing logo...");
      const preparedFile = await prepareLogoUploadFile(file);
      setStatusMessage("Uploading logo...");
      const result = await uploadInvoiceDesignAssetApi("logo", preparedFile);
      setTemplate((prev) => ({ ...prev, logo: result?.file_url || result?.file_path || "" }));
      setStatusMessage("Logo uploaded. Save design to keep this layout.");
      setPreviewOpen(true);
      setSaveState("idle");
    } catch (error) {
      console.error("Failed to upload logo:", error);
      setSaveState("local");
      alert(error?.message || "Could not upload this logo. Please upload a PNG, JPG, or WEBP image.");
    } finally {
      event.target.value = "";
    }
  };

  const handleRemoveLogo = () => {
    setTemplate((prev) => ({ ...prev, logo: "" }));
  };

  const handleSave = async () => {
    const normalized = saveLocalInvoiceDesign(template, globalData);
    setTemplate(normalized);
    setSaveState("saving");
    setStatusMessage("");

    try {
      await saveInvoiceDesignApi(normalized);
      setSaveState("saved");
      setStatusMessage("Design saved for this client.");
    } catch (error) {
      setSaveState("local");
      setStatusMessage("Design saved locally. Backend /invoice_design endpoint is not available yet.");
    }
  };

  const handleFinalize = async () => {
    await handleSave();
    setPreviewOpen(false);
  };

  const handleReset = () => {
    setTemplate(defaultInvoiceDesignTemplate);
    saveLocalInvoiceDesign(defaultInvoiceDesignTemplate, globalData);
    setSaveState("local");
    setStatusMessage("Design reset locally. Save to update backend storage.");
  };

  return (
    <Box
      sx={{
        maxWidth: 1560,
        mx: "auto",
        pb: { xs: 5, xl: 0 },
        minHeight: { xs: "auto", xl: "calc(100vh - 96px)" },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 1.75 },
          mb: 1.25,
          flex: { xl: "0 0 auto" },
          borderRadius: 2,
          border: "1px solid rgba(37, 99, 235, 0.14)",
          background: "linear-gradient(135deg, #ffffff 0%, #f0f7ff 56%, #ecfdf5 100%)",
          boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
          <Box>
            <Typography sx={{ color: "#2563eb", fontSize: 11, fontWeight: 950, textTransform: "uppercase", lineHeight: 1 }}>
              Invoice template
            </Typography>
            <Typography sx={{ fontWeight: 950, color: "#0f172a", fontSize: { xs: 22, md: 26 }, lineHeight: 1.08, mt: 0.35 }}>
              Design invoice print layout
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button component="label" variant="outlined" size="small" startIcon={<UploadFileIcon />} sx={{ textTransform: "none", fontWeight: 900, minHeight: 38 }}>
              Upload Logo
              <input hidden type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={handleLogoUpload} />
            </Button>
            <Button variant="outlined" size="small" startIcon={<VisibilityIcon />} onClick={() => setPreviewOpen(true)} sx={{ textTransform: "none", fontWeight: 900, minHeight: 38 }}>
              Preview
            </Button>
            <Button variant="outlined" size="small" startIcon={<RestartAltIcon />} onClick={handleReset} sx={{ textTransform: "none", fontWeight: 900, minHeight: 38 }}>
              Reset
            </Button>
            <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSave} disabled={saveState === "saving"} sx={{ textTransform: "none", fontWeight: 900, minHeight: 38 }}>
              {saveState === "saving" ? "Saving..." : "Save Design"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "280px minmax(620px, 1fr) 310px" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            maxHeight: { xs: "72vh", xl: "calc(100vh - 174px)" },
            overflow: "auto",
          }}
        >
          <Typography sx={{ fontWeight: 950, color: "#0f172a", mb: 1 }}>Components</Typography>
          {template.logo && (
            <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, border: "1px solid #bfdbfe", bgcolor: "#eff6ff" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: "#1d4ed8", fontSize: 13 }}>Logo active</Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 12 }}>Saved as a file URL in the client design.</Typography>
                </Box>
                <Button size="small" onClick={handleRemoveLogo} sx={{ textTransform: "none", fontWeight: 850 }}>
                  Remove
                </Button>
              </Stack>
            </Box>
          )}
          <Stack spacing={1}>
            {template.blocks.filter((block) => ["company_logo", "seller_contact", "invoice_note", "additional_info"].includes(block.id)).map((block) => (
              <Box
                key={block.id}
                onClick={() => {
                  if (block.id === "company_logo") setSelectedId(block.id);
                }}
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  border: selectedId === block.id ? "1px solid #2563eb" : "1px solid #e2e8f0",
                  bgcolor: selectedId === block.id ? "#eff6ff" : "#fff",
                  cursor: block.id === "company_logo" ? "pointer" : "default",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontWeight: 850, color: "#0f172a" }}>{block.label}</Typography>
                  <Switch
                    size="small"
                    checked={block.visible}
                    onChange={(event) => updateBlock(block.id, { visible: event.target.checked })}
                    onClick={(event) => event.stopPropagation()}
                  />
                </Stack>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 950, color: "#0f172a", mb: 1 }}>Seller Contact</Typography>
          <Stack spacing={1}>
            <TextField
              size="small"
              label="Email override"
              value={template.sellerContactEmail || ""}
              onChange={(event) => updateDesignValue({ sellerContactEmail: event.target.value })}
              placeholder={globalData?.email || "Use DB email"}
              fullWidth
            />
            <TextField
              size="small"
              label="Phone override"
              value={template.sellerContactPhone || ""}
              onChange={(event) => updateDesignValue({ sellerContactPhone: event.target.value })}
              placeholder={globalData?.phone || globalData?.mobile || "Use DB phone"}
              fullWidth
            />
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 950, color: "#0f172a" }}>Item Fields</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addCustomItemColumn} sx={{ textTransform: "none", fontWeight: 850 }}>
              Add
            </Button>
          </Stack>
          <Stack spacing={0.75} sx={{ maxHeight: 260, overflow: "auto", pr: 0.5 }}>
            {(template.itemColumns || []).map((column) => (
              <Stack key={column.key} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 12.5, color: "#0f172a" }}>{getDesignFieldLabel(column.key)}</Typography>
                <Switch size="small" checked={column.visible !== false} onChange={(event) => updateItemColumn(column.key, { visible: event.target.checked })} />
              </Stack>
            ))}
            {(template.customItemColumns || []).map((column) => (
              <Box key={column.id} sx={{ p: 1, border: "1px solid #e2e8f0", borderRadius: 1 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Switch size="small" checked={column.visible !== false} onChange={(event) => updateCustomItemColumn(column.id, { visible: event.target.checked })} />
                  <TextField size="small" label="Label" value={column.label} onChange={(event) => updateCustomItemColumn(column.id, { label: event.target.value })} />
                  <Button size="small" color="error" onClick={() => removeCustomItemColumn(column.id)} sx={{ minWidth: 32, p: 0.5 }}>
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Stack>
                <TextField size="small" label="Printed value" value={column.value} onChange={(event) => updateCustomItemColumn(column.id, { value: event.target.value })} fullWidth sx={{ mt: 1 }} />
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 950, color: "#0f172a" }}>Total Breakdown Section</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addCustomBreakdownRow} sx={{ textTransform: "none", fontWeight: 850 }}>
              Add
            </Button>
          </Stack>
          <Stack spacing={0.85} sx={{ maxHeight: 260, overflow: "auto", pr: 0.5 }}>
            {(template.breakdownRows || []).map((row) => {
              const isDefaultRow = DEFAULT_BREAKDOWN_ROWS.some((defaultRow) => defaultRow.id === row.id);
              return (
                <Box key={row.id} sx={{ p: !isDefaultRow ? 1 : 0, border: !isDefaultRow ? "1px solid #e2e8f0" : "none", borderRadius: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    {!isDefaultRow ? (
                      <TextField size="small" label="Label" value={row.label} onChange={(event) => updateBreakdownRow(row.id, { label: event.target.value })} />
                    ) : (
                      <Typography sx={{ fontSize: 12.5, color: "#0f172a" }}>{row.label}</Typography>
                    )}
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Switch size="small" checked={row.visible !== false} onChange={(event) => updateBreakdownRow(row.id, { visible: event.target.checked })} />
                      {!isDefaultRow && (
                        <Button size="small" color="error" onClick={() => removeCustomBreakdownRow(row.id)} sx={{ minWidth: 32, p: 0.5 }}>
                          <DeleteIcon fontSize="small" />
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                  {!isDefaultRow && (
                    <TextField size="small" label="Value" value={row.value || ""} onChange={(event) => updateBreakdownRow(row.id, { value: event.target.value })} fullWidth sx={{ mt: 1 }} />
                  )}
                </Box>
              );
            })}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            overflow: "auto",
          }}
        >
          <InvoicePreviewCanvas template={template} selectedId={selectedId} onSelect={setSelectedId} onBlockChange={updateBlockWithFlow} globalData={globalData} />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            overflow: { xs: "auto", xl: "hidden" },
          }}
        >
          <Typography sx={{ fontWeight: 950, color: "#0f172a", mb: 1 }}>Logo Position</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<ArrowUpwardIcon />} onClick={() => moveSelected("y", selectedBlock.y - 10)} sx={{ textTransform: "none", fontWeight: 850 }}>
              Up
            </Button>
            <Button size="small" variant="outlined" startIcon={<ArrowDownwardIcon />} onClick={() => moveSelected("y", selectedBlock.y + 10)} sx={{ textTransform: "none", fontWeight: 850 }}>
              Down
            </Button>
            <Button size="small" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => moveSelected("x", selectedBlock.x - 10)} sx={{ textTransform: "none", fontWeight: 850 }}>
              Left
            </Button>
            <Button size="small" variant="outlined" startIcon={<ArrowForwardIcon />} onClick={() => moveSelected("x", selectedBlock.x + 10)} sx={{ textTransform: "none", fontWeight: 850 }}>
              Right
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 950, color: "#0f172a", mb: 1 }}>Selected Block</Typography>
          {selectedBlock && (
            <Stack spacing={1.5}>
              <Chip label={selectedBlock.label} sx={{ fontWeight: 900, bgcolor: "#dbeafe", color: "#1d4ed8" }} />
              <TextField size="small" label="X" type="number" value={selectedBlock.x} onChange={(e) => moveSelected("x", Number(e.target.value))} />
              <TextField size="small" label="Y" type="number" value={selectedBlock.y} onChange={(e) => moveSelected("y", Number(e.target.value))} />
              <TextField size="small" label="Width" type="number" value={selectedBlock.w} onChange={(e) => moveSelected("w", Number(e.target.value))} />
              <TextField size="small" label="Height" type="number" value={selectedBlock.h} onChange={(e) => moveSelected("h", Number(e.target.value))} />
            </Stack>
          )}

        </Paper>
      </Box>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 950, color: "#0f172a" }}>Print Preview</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "#f8fafc", overflow: "auto" }}>
          <InvoicePreviewCanvas template={template} globalData={globalData} preview />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPreviewOpen(false)} sx={{ textTransform: "none", fontWeight: 850 }}>
            Close
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleFinalize} disabled={saveState === "saving"} sx={{ textTransform: "none", fontWeight: 900 }}>
            {saveState === "saving" ? "Saving..." : "Save & Finalize"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Design;
